import { Modal } from 'antd';
import { PageRoutes } from '../../conf/Global';
import { useDeleteAssertionMutation } from '../../graphql/assertion.generated';
import { useDeleteDataProductMutation } from '../../graphql/dataProduct.generated';
import { useDeleteDomainMutation } from '../../graphql/domain.generated';
import { useDeleteGlossaryEntityMutation } from '../../graphql/glossary.generated';
import { useRemoveGroupMutation } from '../../graphql/group.generated';
import { useDeleteTagMutation } from '../../graphql/tag.generated';
import { useRemoveUserMutation } from '../../graphql/user.generated';
import { EntityType } from '../../types.generated';
import { GenericEntityProperties } from '../entity/shared/types';
import EntityRegistry from '../entity/EntityRegistry';

/**
 * Returns a relative redirect path which is used after an Entity has been deleted from it's profile page.
 *
 * @param type the entity type being deleted
 */
export const getEntityProfileDeleteRedirectPath = (type: EntityType, entityData: GenericEntityProperties | null) => {
    const domain = entityData?.domain?.domain;
    switch (type) {
        case EntityType.CorpGroup:
        case EntityType.CorpUser:
        case EntityType.Tag:
            // Return Home.
            return '/';
        case EntityType.Domain:
            return `${PageRoutes.DOMAINS}`;
        case EntityType.GlossaryNode:
        case EntityType.GlossaryTerm:
            // Return to glossary page.
            return '/glossary';
        case EntityType.DataProduct:
            // Return to Data Products tab of the domain it was part of
            if (domain) {
                return `/domain/${domain.urn}/Data Products`;
            }
            return '/';
        default:
            return () => undefined;
    }
};

/**
 * Returns a mutation hook for deleting an entity of a particular type.
 *
 * TODO: Push this back into the entity registry.
 *
 * @param type the entity type being deleted
 */
export const getDeleteEntityMutation = (type: EntityType) => {
    switch (type) {
        case EntityType.CorpGroup:
            return useRemoveGroupMutation;
        case EntityType.CorpUser:
            return useRemoveUserMutation;
        case EntityType.Assertion:
            return useDeleteAssertionMutation;
        case EntityType.Domain:
            return useDeleteDomainMutation;
        case EntityType.Tag:
            return useDeleteTagMutation;
        case EntityType.GlossaryNode:
        case EntityType.GlossaryTerm:
            return useDeleteGlossaryEntityMutation;
        case EntityType.DataProduct:
            return useDeleteDataProductMutation;
        default:
            return () => undefined;
    }
};

interface CheckReadOnlyAssetsOptions {
    entityRegistry: EntityRegistry;
    entityType: EntityType;
    entityData: GenericEntityProperties | any;
    checkFunction: () => Promise<any>;
    modalClassName?: string;
    getModalTitle?: (displayName: string) => string;
    getModalContent?: (entityName: string) => string;
    getModalOkText?: () => string;
}

/**
 * Checks if an entity is attached to read-only assets and shows a warning modal if it is.
 * Returns true if deletion can proceed, false if blocked.
 *
 * @param options Configuration options for the check
 * @returns Promise<boolean> - true if deletion can proceed, false if blocked
 */
export async function checkEntityAttachedToReadOnlyAssets(options: CheckReadOnlyAssetsOptions): Promise<boolean> {
    const {
        entityRegistry,
        entityType,
        entityData,
        checkFunction,
        modalClassName,
        getModalTitle,
        getModalContent,
        getModalOkText,
    } = options;

    try {
        const result: any = await checkFunction();

        if (result?.data?.isEntityAttachedToReadOnlyAssets) {
            const displayName =
                (entityData && entityRegistry.getDisplayName(entityType, entityData)) ||
                entityRegistry.getEntityName(entityType);
            const entityName = entityRegistry.getEntityName(entityType);

            Modal.warning({
                centered: true,
                closable: true,
                maskClosable: true,
                okText: getModalOkText ? getModalOkText() : 'Ok',
                className: modalClassName || 'f-borderless-modal',
                okButtonProps: {
                    type: undefined,
                    danger: true,
                },
                title: getModalTitle ? getModalTitle(displayName) : `Cannot Delete ${displayName}`,
                content: getModalContent
                    ? getModalContent(entityName || '')
                    : `This ${entityName || ''} is attached to read-only assets and cannot be deleted.`,
            });
            return false;
        }

        return true;
    } catch (error) {
        // If check fails, allow deletion to proceed (fail open)
        // Error handling can be added here if needed
        return true;
    }
}
